import React, { useState} from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  styled, 
  TextField, 
  IconButton, 
  Grid, 
  Card, 
  CardContent, 
  Menu, 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  ImageListItemBar
} from '@mui/material';
import { 
  Mic, 
  Send, 
  AspectRatio, 
  Style, 
  HighQuality, 
  Check, 
  Download,
  Collections,
  ZoomIn
} from '@mui/icons-material';
//import { generateImage, getImageHistory, ImageMetadata } from '@/api/images';
import { useImageHistoryQuery } from '@/features/images/hooks/useImagesQuery';
import { useGenerateImageMutation } from '@/features/images/hooks/useImagesMutations';
import ResponsivePage from '@/components/ResponsivePage';

// Styled components
const FloatingInput = styled(Paper)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  background: '#fff',
  maxWidth: '800px',
  width: '100%',
  margin: '0 auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
  borderRadius: '12px',
  flexDirection: 'column',
});

const SampleCard = styled(Card)({
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  },
});

// Sample prompts data
const samplePrompts = [
  {
    title: 'Nature photography emulation',
    description: 'A hyperrealistic shot of an eagle\'s eye',
    icon: '🦅'
  },
  {
    title: 'Earth from above',
    description: 'Earth from international space station',
    icon: '🌎'
  },
  {
    title: 'Bossard Project Roadmap',
    description: 'Generate a visual project roadmap infographic for Bossard',
    icon: '📊'
  },
  {
    title: 'Bossard Marketing Strategy',
    description: 'Visual representation of Bossard\'s marketing strategy',
    icon: '🚀'
  },

];

// Dimension options
const dimensionOptions = [
  { value: '1024x1024', label: '1024x1024', selected: true },
  { value: '1024x1792', label: '1024x1792', selected: false },
  { value: '1792x1024', label: '1792x1024', selected: false }
];

// Style options
const styleOptions = [
  { value: 'vivid', label: 'vivid', selected: false },
  { value: 'natural', label: 'natural', selected: true }
];

// Quality options
const qualityOptions = [
  { value: 'standard', label: 'standard', selected: true },
  { value: 'hd', label: 'hd', selected: false }
];

const GenerateImagePage: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [lastPrompt, setLastPrompt] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  
  const generateImageMutation = useGenerateImageMutation();
  
  // Menu state
  const [dimensionsAnchorEl, setDimensionsAnchorEl] = useState<null | HTMLElement>(null);
  const [styleAnchorEl, setStyleAnchorEl] = useState<null | HTMLElement>(null);
  const [qualityAnchorEl, setQualityAnchorEl] = useState<null | HTMLElement>(null);
  
  // Selected options
  const [selectedDimension, setSelectedDimension] = useState(dimensionOptions.find(o => o.selected)?.value || '1024x1024');
  const [selectedStyle, setSelectedStyle] = useState(styleOptions.find(o => o.selected)?.value || 'natural');
  const [selectedQuality, setSelectedQuality] = useState(qualityOptions.find(o => o.selected)?.value || 'standard');
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Image history state
  const {data: imageHistory, isLoading: loadingHistory} = useImageHistoryQuery()
  //const [imageHistory, setImageHistory] = useState<ImageMetadata[]>([]);
  //const [loadingHistory, setLoadingHistory] = useState(false);

  // Speech recognition
  const handleMicClick = () => {
    setIsRecording(!isRecording);
    // Implement speech recognition logic here
  };

  // Sample prompts
  const handleSampleClick = (description: string) => {
    setPrompt(description);
  };
  // Generate image
  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    try {
      const result = await generateImageMutation.mutateAsync({
        prompt,
        size: selectedDimension as '1024x1024' | '1024x1792' | '1792x1024',
        style: selectedStyle as 'natural' | 'vivid',
        quality: selectedQuality as 'standard' | 'hd'
      });
      
      if (result.success && result.url) {
        setGeneratedImage(result.url);
        setLastPrompt(prompt);
      }
    } catch (err) {
      console.error('Error generating image:', err);
      setLastPrompt(null);
    }
  };

  // Menu handlers
  const handleDimensionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setDimensionsAnchorEl(event.currentTarget);
  };
  
  const handleStyleClick = (event: React.MouseEvent<HTMLElement>) => {
    setStyleAnchorEl(event.currentTarget);
  };
  
  const handleQualityClick = (event: React.MouseEvent<HTMLElement>) => {
    setQualityAnchorEl(event.currentTarget);
  };

  const handleDimensionsClose = () => {
    setDimensionsAnchorEl(null);
  };
  
  const handleStyleClose = () => {
    setStyleAnchorEl(null);
  };
  
  const handleQualityClose = () => {
    setQualityAnchorEl(null);
  };

  const handleDimensionSelect = (dimension: string) => {
    setSelectedDimension(dimension);
    handleDimensionsClose();
  };
  
  const handleStyleSelect = (style: string) => {
    setSelectedStyle(style);
    handleStyleClose();
  };
  
  const handleQualitySelect = (quality: string) => {
    setSelectedQuality(quality);
    handleQualityClose();
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch image history when component mounts or tab changes to history
  /*useEffect(() => {
    const fetchImageHistory = async () => {
      if (tabValue === 1) { // History tab
        setLoadingHistory(true);
        try {
          const images = await getImageHistory(20);
          setImageHistory(images);
        } catch (error) {
          console.error('Error fetching image history:', error);
          setError('Failed to load image history');
        } finally {
          setLoadingHistory(false);
        }
      }
    };

    fetchImageHistory();
  }, [tabValue]); // Re-run when tab changes
  */
  const handleDownload = async () => {
    if (!generatedImage) return;

    try {
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated_image.jpg'); // Or use a more dynamic name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error downloading image:', downloadError);
    }
  };

  return (
    <ResponsivePage
      alignItems="center"
      position="relative"
      sx={{
        bgcolor: 'white',
        backdropFilter: 'blur(10px)',
        pb: 6,
      }}
    >
      
         <Box 
        sx={{ 
          textAlign: 'center', 
          py: 4,
          width: '100%',
          maxWidth: 1200,
          mx: 'auto'
        }}
      >
        <Typography variant="h4" fontWeight={700} color="primary.main">
          GENERATE IMAGES
        </Typography>
        
        {/* Tab Navigation */}
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          centered
          sx={{ mt: 2, mb: 1 }}
        >
          <Tab 
            icon={<Send fontSize="small" />} 
            label="Generate New" 
            iconPosition="start"
          />
          <Tab 
            icon={<Collections fontSize="small" />} 
            label="Past Images" 
            iconPosition="start"
          />
        </Tabs>
        
        {tabValue === 0 && (
          <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
            Start with a sample text prompt
          </Typography>
        )}
      </Box>

      {/* Generated Image Display - MODIFIED FOR ROW LAYOUT */}
      {generatedImage && (
        <Paper 
          elevation={3}
          sx={{ 
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            width: '100%',
            maxWidth: { xs: '100%', sm: 760, md: 980 },
            overflow: 'hidden',
            mx: 'auto',
            p: { xs: 1, sm: 1.5 },
            gap: { xs: 1, md: 2 },
            borderRadius: 2,
          }}
        >
          {/* Image on the left */}
          <Box 
            component="img" 
            src={generatedImage} 
            alt="Generated image based on prompt"
            sx={{
              width: { xs: '100%', md: '55%' },
              height: 'auto',
              maxHeight: { xs: 240, sm: 300, md: 340 },
              objectFit: 'contain',
              borderRadius: 1,
              mr: { xs: 0, md: 1 },
            }}
          />
          {/* Text content and Download button on the right */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            width: { xs: '100%', md: '45%' },
            overflowY: 'visible',
          }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', textAlign: 'left' }}>
              Generated using {selectedStyle} style, {selectedQuality} quality, {selectedDimension} resolution.
            </Typography>
            {lastPrompt && (
              <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary', textAlign: 'left' }}>
                Prompt: "{lastPrompt}"
              </Typography>
            )}
            <Box sx={{ mt: 1, textAlign: 'left' }}>
              <Tooltip title="Download Image (JPG)">
                <IconButton onClick={handleDownload} color="primary" size="small">
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      )}

     {/* Tab Content */}
      {tabValue === 0 ? (
        <>
          {/* Generate Tab Content */}
          {/* Conditionally render Sample Prompts */}
          {!generatedImage && (
            <Grid container spacing={3} sx={{ mb: 4, width: '100%', maxWidth: 1200, mx: 'auto' }}>
              {samplePrompts.map((sample, index) => (
                <Grid key={index} size={{ md: 6, sm: 6, xs:12}}>
                  <SampleCard 
                    onClick={() => handleSampleClick(sample.description)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent>
                      <Box display="flex" mb={1}>
                        <Typography variant="h1" sx={{ mr: 1, fontSize: 24 }}>
                          {sample.icon}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {sample.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {sample.description}
                      </Typography>
                    </CardContent>
                  </SampleCard>
                </Grid>
              ))}
            </Grid>      
          )}
        </>
      ) : (
        /* Past Images Tab Content */
        <Box 
          sx={{ 
            width: '100%', 
            maxWidth: 1200, 
            mx: 'auto', 
            my: { xs: 2, md: 4 },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 1.5, sm: 2, md: 3 },
            pb: { xs: 3, md: 6 },
          }}
        >
          {loadingHistory ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={5}>
              <CircularProgress />
            </Box>
          ) : imageHistory?.length === 0 ? (
            <Box textAlign="center" p={5}>
              <Typography variant="h6" color="text.secondary">
                No images found in your history
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Generate your first image in the Generate tab
              </Typography>
            </Box>
          ) : (
            <ImageList 
              sx={{ 
                width: '100%', 
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(250px, 1fr))!important', // Mobile: smaller cards
                  sm: 'repeat(auto-fill, minmax(280px, 1fr))!important',  // Tablet and up
                  md: 'repeat(auto-fill, minmax(300px, 1fr))!important'   // Desktop
                },
                gap: '24px!important', // Increased gap between items
                overflow: 'visible', // Allow hover effects to extend beyond container
                pb: 4 // Add padding at the bottom of the grid
              }}
              gap={24} // Increased gap
            >
              {imageHistory!.map((image) => (
                <ImageListItem 
                  key={image.id} 
                  sx={{ 
                    boxShadow: 2, 
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    mb: 3, // Increased bottom margin between rows
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      zIndex: 1 // Ensure hover effect appears on top
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '100%', // 1:1 Aspect ratio
                      width: '100%',
                      height: 0,
                      overflow: 'hidden',
                      backgroundColor: 'rgba(0,0,0,0.05)' // Light background while loading
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.prompt.substring(0, 50)}
                      loading="lazy"
                      style={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </Box>
                  <ImageListItemBar
                    title={
                      <Tooltip title={image.prompt}>
                        <Typography noWrap variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                          {image.prompt.substring(0, 35)}
                          {image.prompt.length > 35 ? '...' : ''}
                        </Typography>
                      </Tooltip>
                    }
                    subtitle={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ fontSize: '0.7rem' }}>
                          {new Date(image.created_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ fontSize: '0.7rem' }}>
                          {image.style} • {image.quality}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiImageListItemBar-titleWrap': {
                        padding: '8px 12px',
                      },
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.3), transparent)'
                    }}
                    actionIcon={
                      <Box sx={{ display: 'flex' }}>
                        {/* View full size button */}
                        <Tooltip title="View full size">
                          <IconButton
                            sx={{ color: 'white' }}
                            aria-label={`view full size image ${image.prompt}`}
                            onClick={() => {
                              window.open(image.url, '_blank');
                            }}
                          >
                            <ZoomIn fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {/* Download button */}
                        <Tooltip title="Download image">
                          <IconButton
                            sx={{ color: 'white', mr: 1 }}
                            aria-label={`download image ${image.prompt}`}
                            onClick={() => {
                              const downloadImage = async () => {
                                try {
                                  const response = await fetch(image.url);
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  // Use first 20 chars of prompt as filename
                                  const filename = image.prompt.substring(0, 20).replace(/[^a-z0-9]/gi, '_').toLowerCase();
                                  link.setAttribute('download', `${filename}.jpg`);
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Error downloading image:', error);
                                }
                              };
                              downloadImage();
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Box>
      )}
      
      {/* Input Area - Only show in Generate tab */}
      {tabValue === 0 && (
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}
      sx={{ 
        width: '100%', 
        maxWidth: 800, 
        mb: 4, 
        mx: 'auto',
        mt: generatedImage ? '20vh' : 4 // Push down if image is generated
      }}>
        <FloatingInput>
          {/* Text Input */}
          <Box sx={{ width: '100%', p: 1 }}>   
            <TextField
              fullWidth
              multiline
              placeholder="Describe the image you want to generate..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              variant="standard"
              slotProps={{ 
                input: {
                  disableUnderline: true
                }
              }}
              sx={{ '& .MuiInputBase-root': { fontSize: '1.1rem' } }}
              rows={4}
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            width: '100%', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
            paddingTop: 1,
            mt: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Voice Input */}
              <Tooltip title="Voice to text">
                <IconButton 
                  type="button" 
                  color="primary" 
                  onClick={handleMicClick}
                  disabled={generateImageMutation.isPending}
                >
                  <Mic color={isRecording ? 'error' : 'inherit'} />
                </IconButton>
              </Tooltip>
              
              {/* Dimensions Dropdown */}
              <Tooltip title="Image dimensions">
                <IconButton
                  color="primary"
                  onClick={handleDimensionsClick}
                  disabled={generateImageMutation.isPending}
                >
                  <AspectRatio />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={dimensionsAnchorEl}
                open={Boolean(dimensionsAnchorEl)}
                onClose={handleDimensionsClose}
                slotProps={{
                  paper: {
                    sx: {
                      width: 180,
                      maxWidth: '100%',
                    }
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Size
                </Typography>
                {dimensionOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    onClick={() => handleDimensionSelect(option.value)}
                  >
                    {selectedDimension === option.value && (
                      <ListItemIcon>
                        <Check fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText 
                      inset={selectedDimension !== option.value}
                      primary={option.label} 
                    />
                  </MenuItem>
                ))}
              </Menu>
              
              {/* Style Dropdown */}
              <Tooltip title="Image style">
                <IconButton
                  color="primary"
                  onClick={handleStyleClick}
                  disabled={generateImageMutation.isPending}
                >
                  <Style />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={styleAnchorEl}
                open={Boolean(styleAnchorEl)}
                onClose={handleStyleClose}
                slotProps={{
                  paper: {
                    sx: {
                      width: 180,
                      maxWidth: '100%',
                    }
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Style
                </Typography>
                {styleOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    onClick={() => handleStyleSelect(option.value)}
                  >
                    {selectedStyle === option.value && (
                      <ListItemIcon>
                        <Check fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText 
                      inset={selectedStyle !== option.value}
                      primary={option.label} 
                    />
                  </MenuItem>
                ))}
              </Menu>
              
              {/* Quality Dropdown */}
              <Tooltip title="Image quality">
                <IconButton
                  color="primary"
                  onClick={handleQualityClick}
                  disabled={generateImageMutation.isPending}
                >
                  <HighQuality />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={qualityAnchorEl}
                open={Boolean(qualityAnchorEl)}
                onClose={handleQualityClose}
                slotProps={{
                  paper: {
                    sx: {
                      width: 180,
                      maxWidth: '100%',
                    }
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Quality
                </Typography>
                {qualityOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    onClick={() => handleQualitySelect(option.value)}
                  >
                    {selectedQuality === option.value && (
                      <ListItemIcon>
                        <Check fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText 
                      inset={selectedQuality !== option.value}
                      primary={option.label} 
                    />
                  </MenuItem>
                ))}
              </Menu>
            </Box>
              {/* Generate Button */}
            <IconButton 
              type="submit" 
              color="primary" 
              disabled={!prompt.trim() || generateImageMutation.isPending}
              sx={{ ml: 1 }}
            >
              {generateImageMutation.isPending ? <CircularProgress size={24} /> : <Send />}
            </IconButton>
          </Box>
        </FloatingInput>
      </Box>
      )}
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!generateImageMutation.error} 
        autoHideDuration={6000} 
        onClose={() => {
          // Reset error by creating a new mutation instance or use mutation state
          generateImageMutation.reset();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => generateImageMutation.reset()} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {generateImageMutation.error?.message || 'Image generation failed'}
        </Alert>
      </Snackbar>
    </ResponsivePage>
  );
};

export default GenerateImagePage;
